import React, { useState, useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Button, Dimmer, Header, Icon, Image, Label, List, Loader, Modal, Segment
} from 'semantic-ui-react';
import { useStore } from '../../app/stores/store';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';

interface Props {
    teamId: string;
    /** Optional: control modal open state externally (e.g. from TeamDashboard shortcut) */
    externalOpen?: boolean;
    onExternalClose?: () => void;
}

export default observer(function InviteToTeamModal({ teamId, externalOpen, onExternalClose }: Props) {
    const { teamStore } = useStore();
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');

    // Sync with external open signal
    useEffect(() => {
        if (externalOpen) setOpen(true);
    }, [externalOpen]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(
        debounce((q: string) => {
            teamStore.searchUsersToInvite(teamId, q);
        }, 400),
        [teamId]
    );

    useEffect(() => {
        if (open) debouncedSearch(query);
    }, [query, open, debouncedSearch]);

    const handleClose = () => {
        setOpen(false);
        setQuery('');
        teamStore.clearInvitableUsers();
        onExternalClose?.();
    };

    return (
        <Modal
            trigger={
                <Button
                    size="small"
                    primary
                    onClick={() => setOpen(true)}
                    style={{ borderRadius: 50 }}
                >
                    <Icon name="user plus" />
                    {t('teams.invitePlayer')}
                </Button>
            }
            open={open}
            onClose={handleClose}
            size="small"
            closeIcon
        >
            <Modal.Header>
                <Icon name="users" />
                {t('teams.inviteModal.title')}
            </Modal.Header>

            <Modal.Content scrolling style={{ minHeight: 360 }}>
                {/* Search input */}
                <Segment>
                    <Header as="h4">
                        <Icon name="search" />
                        {t('teams.inviteModal.searchLabel')}
                    </Header>
                    <div className="ui fluid input">
                        <input
                            placeholder={t('teams.inviteModal.searchPlaceholder')}
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            autoFocus
                        />
                    </div>
                </Segment>

                {teamStore.searchingUsers && (
                    <Dimmer active inverted>
                        <Loader inverted>{t('teams.loading')}</Loader>
                    </Dimmer>
                )}

                {!teamStore.searchingUsers && teamStore.invitableUsers.length > 0 && (
                    <List divided relaxed>
                        {teamStore.invitableUsers.map(user => (
                            <List.Item key={user.userId} style={{ padding: '10px 0' }}>
                                <Image avatar src={user.imageUrl || '/assets/user.png'} />
                                <List.Content>
                                    <List.Header style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        {user.fullName}
                                        {user.isAlreadyMember && (
                                            <Label size="tiny" color="green">
                                                {t('teams.inviteModal.alreadyMember')}
                                            </Label>
                                        )}
                                        {user.isAlreadyInvited && (
                                            <Label size="tiny" color="yellow">
                                                {t('teams.inviteModal.alreadyInvited')}
                                            </Label>
                                        )}
                                    </List.Header>
                                </List.Content>
                                {!user.isAlreadyMember && !user.isAlreadyInvited && (
                                    <List.Content floated="right">
                                        <Button
                                            size="mini" primary
                                            loading={teamStore.submitting}
                                            disabled={teamStore.submitting}
                                            onClick={() => teamStore.inviteMember(teamId, user.userId)}
                                        >
                                            <Icon name="send" />
                                            {t('teams.inviteModal.invite')}
                                        </Button>
                                    </List.Content>
                                )}
                            </List.Item>
                        ))}
                    </List>
                )}

                {!teamStore.searchingUsers && query.length >= 2 && teamStore.invitableUsers.length === 0 && (
                    <p style={{ color: '#888', textAlign: 'center', padding: '30px 0' }}>
                        <Icon name="search" />
                        {t('teams.inviteModal.noResults')}
                    </p>
                )}

                {query.length < 2 && (
                    <p style={{ color: '#aaa', textAlign: 'center', padding: '30px 0', fontSize: '0.9em' }}>
                        {t('teams.inviteModal.typeToSearch')}
                    </p>
                )}
            </Modal.Content>

            <Modal.Actions>
                <Button onClick={handleClose}>
                    <Icon name="close" />
                    {t('teams.cancel')}
                </Button>
            </Modal.Actions>
        </Modal>
    );
});